import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { PageHero } from "../components/PageHero";
import { CTA } from "./Home";
import { IMAGES, CONTACT, VALUES, TIMELINE } from "../data/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testid="about-hero"
        eyebrow="About Sora Speech Co."
        title="Where care, science, and connection meet."
        subtitle="A mobile paediatric speech pathology practice founded on warmth, evidence, and a deep belief in every child's potential."
        image={IMAGES.sunlit}
      />

      {/* Bio */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="rounded-[28px] overflow-hidden shadow-[0_24px_60px_rgba(63,88,115,0.14)]">
                <img data-testid="about-portrait" src={IMAGES.portrait} alt={CONTACT.practitioner} className="w-full h-[520px] object-cover object-top" />
              </div>
              <div className="absolute -bottom-6 -right-4 md:-right-6 bg-sora-warm rounded-[22px] shadow-[0_16px_40px_rgba(63,88,115,0.12)] px-6 py-4">
                <p className="font-display text-xl text-sora-slate">{CONTACT.practitioner.split(" ")[0]}</p>
                <p className="text-xs text-sora-charcoal/60 mt-1">Speech Pathologist & Founder</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Meet {CONTACT.practitioner.split(" ")[0]}</p>
            <h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug">A calm, steady presence for children and families.</h2>
            <p className="mt-5 text-sora-charcoal/75 leading-relaxed">
              {CONTACT.practitioner.split(" ")[0]} is a certified practising speech pathologist supporting children across Melbourne. She believes therapy should be meaningful, engaging, and enjoyable for children — while empowering families with practical strategies they can use every day.
            </p>
            <p className="mt-4 text-sora-charcoal/75 leading-relaxed">
              She founded Sora Speech Co. with a simple belief: that the best therapy happens in the places children feel most themselves — at home, at kinder, at school, and out in their community.
            </p>
            <div className="mt-7 rounded-[20px] bg-sora-warm border border-sora-stone/30 p-6">
              <p className="text-xs uppercase tracking-[0.18em] text-sora-blue font-bold mb-2">Qualifications</p>
              <p className="text-sora-slate font-medium">{CONTACT.qualifications}</p>
              <p className="mt-1 text-sm text-sora-charcoal/70">Certified Practising Member · Experienced NDIS provider</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-sora-slate text-sora-warm mt-10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] text-sora-sage font-bold mb-5">Our Mission</p>
            <h2 className="font-display text-3xl sm:text-4xl leading-snug">To help every child find their voice — and to walk beside their family with warmth, evidence, and genuine care, every step of the way.</h2>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl"><p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Our Values</p><h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug">Four principles that guide everything we do.</h2></Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="rounded-[24px] bg-sora-warm border border-sora-stone/25 p-8 h-full hover:-translate-y-1.5 transition-transform duration-300">
                  <span className="grid place-items-center h-14 w-14 rounded-full bg-sora-sage/45 text-sora-slate"><Icon name={v.icon} size={26} /></span>
                  <h3 className="font-display text-2xl text-sora-slate mt-5">{v.title}</h3>
                  <p className="mt-3 text-sora-charcoal/75 leading-relaxed text-sm">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-sora-warm/60">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Reveal><p className="text-xs uppercase tracking-[0.24em] text-sora-blue font-bold mb-4">Journey</p><h2 className="font-display text-3xl sm:text-4xl text-sora-slate leading-snug mb-12">A career built around children.</h2></Reveal>
          <div className="relative pl-8 border-l border-sora-stone/50">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.05}>
                <div className="relative pb-10 last:pb-0">
                  <span className="absolute -left-[41px] top-1 grid place-items-center h-5 w-5 rounded-full bg-sora-blue ring-4 ring-sora-bg" />
                  <p className="font-display text-2xl text-sora-blue">{t.year}</p>
                  <p className="mt-1 text-sora-charcoal/75 leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
}
